import { useConfirmation } from '@/hooks/useConfirmation';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import {
  v2,
  type SubmissionRequirementsData,
  type DocumentTemplate,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { TEMPLATE_TYPES } from '@/constants/templateTypes';
import { regionOfTemplate } from '@/lib/templateRender';
import { RequirementEditModal } from './RequirementEditModal';
import { statusOf, type EditableRequirement } from './requirementEdit';
import {
  formatRulesApi,
  SLOT_FORMAT_LABELS,
  type FormatRulesResponse,
} from '@/lib/api/formatRules';

/**
 * Phase form-templates-submission-requirements-tab
 *
 * Tab 3 — "Submission Requirements"
 *
 * Renders a matrix of required forms + templates per region + submission type.
 * System defaults (TenantId=null) show a "Default" badge; tenant overrides show
 * a "Custom" badge with a delete button.
 *
 * Required (✅) / Conditional (🟡 with tooltip showing condition text) / Optional (⚪)
 * badges are shown for each row.  The "+ Add row" button opens an inline add form.
 */

export interface SubmissionRequirementsTabProps {
  canWrite: boolean;
  canDelete: boolean;
}

const REGIONS = [
  'FDA',
  'Health Canada',
  'EMA',
  'MHRA',
  'PMDA',
  'TGA',
  'Swissmedic',
] as const;

const SUBMISSION_TYPES: Record<string, string[]> = {
  FDA: ['NDA', 'BLA', 'ANDA', 'IND'],
  'Health Canada': ['NDS', 'SNDS', 'CTA', 'ANDS', 'NC'],
  EMA: ['MAA', 'Variation', 'LineExtension'],
  MHRA: ['MAA', 'Variation'],
  PMDA: ['NDA', 'Variation'],
  TGA: ['NMA', 'Variation'],
  Swissmedic: ['MA', 'Variation'],
};

// ── Styles ────────────────────────────────────────────────────────────────────
const thStyle: CSSProperties = {
  padding: '8px 12px',
  background: 'var(--primary, #1a3d6b)',
  color: '#fff',
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: '7px 12px',
  fontSize: 12,
  verticalAlign: 'middle',
  borderBottom: '1px solid var(--border)',
};

const sectionHeaderStyle: CSSProperties = {
  fontWeight: 700,
  fontSize: 13,
  padding: '10px 12px',
  background: 'var(--surface2)',
  borderBottom: '1px solid var(--border)',
  letterSpacing: '0.03em',
};

// phase-forms-workflow-follow-up (Item C) — row action button styles.
const smallActionBtn = (color: string): CSSProperties => ({
  padding: '3px 10px',
  background: 'none',
  border: `1px solid ${color}`,
  borderRadius: 4,
  color,
  cursor: 'pointer',
  fontSize: 11,
});
const editBtnStyle = smallActionBtn('var(--accent, #2563eb)');
const resetBtnStyle = smallActionBtn('#b45309');
const deleteBtnStyle = smallActionBtn('var(--red, #dc2626)');

const badgeStyle = (color: string): CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  background: color,
  color: '#fff',
  whiteSpace: 'nowrap',
});

function RequiredBadge({
  isRequired,
  conditional,
}: {
  isRequired: boolean;
  conditional: string | null;
}): ReactElement {
  if (isRequired) {
    return <span style={badgeStyle('#2e7d32')}>✅ Required</span>;
  }
  if (conditional) {
    return (
      <div>
        <span title={conditional} style={badgeStyle('#b45309')}>🟡 Conditional</span>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Required when: {conditional}</div>
      </div>
    );
  }
  return <span style={{ ...badgeStyle('#6b7280'), opacity: 0.85 }}>⚪ Optional</span>;
}

function SourceBadge({ tenantId }: { tenantId: string | null }): ReactElement {
  if (tenantId === null) {
    return (
      <span
        style={{
          fontSize: 10,
          padding: '2px 7px',
          borderRadius: 8,
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          color: 'var(--text-muted)',
        }}
      >
        Default
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 8,
        background: '#eff6ff',
        border: '1px solid #93c5fd',
        color: '#1d4ed8',
      }}
    >
      Custom
    </span>
  );
}

// ── Add-row form ──────────────────────────────────────────────────────────────
interface AddFormState {
  region: string;
  submissionType: string;
  templateType: string;
  templateName: string;
  isRequired: boolean;
  conditional: string;
  notes: string;
  /** R1 — '' = applies to all activities of the region+type. */
  activityCode: string;
}

function makeEmptyForm(region: string, submissionType: string): AddFormState {
  return {
    region,
    submissionType,
    templateType: '',
    templateName: '',
    isRequired: true,
    conditional: '',
    notes: '',
    activityCode: '',
  };
}

// ── Main component ────────────────────────────────────────────────────────────
export function SubmissionRequirementsTab({
  canWrite,
  canDelete,
}: SubmissionRequirementsTabProps): ReactElement {
  const { ask, dialog } = useConfirmation();
  const [region, setRegion] = useState<string>(REGIONS[0]);
  // R1 — activity filter ('' = all activities) + the catalogue-driven
  // activity list for the selected region + application type.
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [activities, setActivities] = useState<ReadonlyArray<{ code: string; name: string }>>([]);
  const [submissionType, setSubmissionType] = useState<string>(
    SUBMISSION_TYPES[REGIONS[0]]![0]!,
  );
  const [data, setData] = useState<SubmissionRequirementsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add-row modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddFormState>(() =>
    makeEmptyForm(REGIONS[0], SUBMISSION_TYPES[REGIONS[0]]![0]!),
  );
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Phase phase-form-templates-ui-fixes (FT-SR-5) — existing document
  // templates, loaded once, used to power the Template-name typeahead and
  // the "no matching template" warning in the add-row form.
  const [allTemplates, setAllTemplates] = useState<DocumentTemplate[]>([]);
  useEffect(() => {
    let cancelled = false;
    v2.documentTemplates
      .list()
      .then((rows) => {
        if (!cancelled) setAllTemplates(rows);
      })
      .catch(() => {
        // Non-fatal — the field degrades to a plain text input.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Phase phase-fix-admin-forms-tab-crash — `getRequirements()` resolves to
      // the requirements object itself (apiFetch unwraps the { ok, data }
      // envelope); reading `res.data` here was undefined at runtime, the same
      // latent bug that crashed the Admin Forms tab.
      const res = await v2.submissionRequirements.getRequirements(
        region,
        submissionType,
        activityFilter || null,
      );
      setData(res ? { ...res, forms: res.forms ?? [], templates: res.templates ?? [] } : null);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load requirements'));
    } finally {
      setLoading(false);
    }
  }, [region, submissionType, activityFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // phase-region-format-rules-and-docx-links — the region's document FORMAT
  // rules (e.g. HC Module 1.3 → Word required). Absence of a row = PDF.
  const [formatRules, setFormatRules] = useState<FormatRulesResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    formatRulesApi
      .list(region)
      .then((r) => {
        if (!cancelled) setFormatRules(r);
      })
      .catch(() => {
        if (!cancelled) setFormatRules(null);
      });
    return () => {
      cancelled = true;
    };
  }, [region]);

  // R1 — fetch the activity vocabulary for the selected region + type.
  useEffect(() => {
    let cancelled = false;
    setActivityFilter('');
    v2.submissionTypes
      .list({ region, applicationType: submissionType })
      .then((res) => {
        if (cancelled) return;
        const types = res.submissionTypes ?? [];
        setActivities(types.map((t) => ({ code: t.code, name: t.name })));
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [region, submissionType]);

  const handleRegionChange = (r: string): void => {
    const types = SUBMISSION_TYPES[r] ?? [];
    setRegion(r);
    setSubmissionType(types[0] ?? '');
  };

  // ISS-012 — destructive delete/reset go through the shared confirmation
  // dialog (useConfirmation), not the native browser confirm().
  const handleDelete = async (id: string): Promise<void> => {
    if (!(await ask({ title: 'Delete requirement', message: 'Delete this custom requirement entry?', confirmLabel: 'Delete', variant: 'destructive' }))) return;
    try {
      await v2.submissionRequirements.deleteTemplate(id);
      void load();
    } catch (e) {
      setError(formatApiError(e, 'Failed to delete entry'));
    }
  };

  // phase-forms-workflow-follow-up (Item C) — edit + reset.
  const [editRow, setEditRow] = useState<EditableRequirement | null>(null);

  const handleReset = async (catalog: 'form' | 'template', id: string): Promise<void> => {
    if (!(await ask({ title: 'Reset requirement', message: 'Reset to the built-in default? Your override will be removed.', confirmLabel: 'Reset', variant: 'destructive' }))) return;
    try {
      await v2.submissionRequirements.resetOverride(catalog, id);
      void load();
    } catch (e) {
      setError(formatApiError(e, 'Failed to reset override'));
    }
  };

  const editableFromTemplate = (t: SubmissionRequirementsData['templates'][number]): EditableRequirement => ({
    catalog: 'template',
    id: t.id,
    isBuiltInOrOverride: t.tenantId === null || !!t.isOverride,
    baselineId: t.isOverride && t.overridesCatalogId ? t.overridesCatalogId : t.id,
    name: t.templateName,
    status: statusOf(t.isRequired, t.conditional),
    conditional: t.conditional,
    notes: t.notes,
    activityCode: t.activityCode ?? null,
    contextLabel: `${region} · ${submissionType} · ${t.templateType}`,
  });

  const editableFromForm = (f: SubmissionRequirementsData['forms'][number]): EditableRequirement => ({
    catalog: 'form',
    id: f.id,
    isBuiltInOrOverride: f.tenantId === null || !!f.isOverride,
    baselineId: f.isOverride && f.overridesCatalogId ? f.overridesCatalogId : f.id,
    name: f.displayName,
    status: statusOf(f.required, f.conditional ?? null),
    conditional: f.conditional ?? null,
    notes: f.notes ?? null,
    activityCode: f.activityCode ?? null,
    contextLabel: `${region} · ${submissionType} · ${f.formType}`,
  });

  const handleAddSubmit = async (): Promise<void> => {
    setAddSaving(true);
    setAddError(null);
    try {
      await v2.submissionRequirements.addTemplate({
        region: addForm.region,
        submissionType: addForm.submissionType,
        templateType: addForm.templateType,
        templateName: addForm.templateName,
        isRequired: addForm.isRequired,
        conditional: addForm.conditional || null,
        notes: addForm.notes || null,
        activityCode: addForm.activityCode || null,
      });
      setAddOpen(false);
      setAddForm(makeEmptyForm(region, submissionType));
      void load();
    } catch (e) {
      setAddError(formatApiError(e, 'Failed to add entry'));
    } finally {
      setAddSaving(false);
    }
  };

  const formsCount = data?.forms.length ?? 0;
  const templatesCount = data?.templates.length ?? 0;

  const filteredTemplates = useMemo(
    () => data?.templates ?? [],
    [data],
  );

  const availableTypes = SUBMISSION_TYPES[region] ?? [];

  return (
    <section aria-label="Submission Requirements" data-testid="submission-requirements-tab">
      {dialog}
      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <strong>Region:</strong>
          <select
            value={region}
            onChange={(e) => handleRegionChange(e.target.value)}
            data-testid="region-select"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
          >
            {REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <strong>Type:</strong>
          <select
            value={submissionType}
            onChange={(e) => setSubmissionType(e.target.value)}
            data-testid="type-select"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
          >
            {availableTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>

        {/* R1 — activity filter, populated from SubmissionTypesCatalog for the
            selected region + type. 'All activities' shows every row (activity-
            specific rows included); picking an activity resolves exactly what a
            sequence with that activity would get. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <strong>Activity:</strong>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            data-testid="activity-select"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
          >
            <option value="">All activities</option>
            {activities.map((a) => (
              <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
            ))}
          </select>
        </label>

        {canWrite && (
          <button
            type="button"
            data-testid="add-row-btn"
            onClick={() => {
              setAddForm(makeEmptyForm(region, submissionType));
              setAddError(null);
              setAddOpen(true);
            }}
            style={{
              marginLeft: 'auto',
              padding: '5px 14px',
              background: 'var(--primary, #1a3d6b)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            + Add row
          </button>
        )}
      </div>

      {/* phase-region-format-rules-and-docx-links — the region's document
          FORMAT rules. Some regional slots legitimately require Word: Health
          Canada edits the Product Monograph / labelling during review, so a
          PDF-only PM is refused. Default everywhere else: PDF. The compiler
          and the FMT-SLOT-01 validator enforce exactly these rows. */}
      {formatRules !== null && (
        <div
          data-testid="format-rules-card"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            background: 'var(--surface)',
            fontSize: 13,
          }}
        >
          <strong>Document format rules — {region}</strong>
          <div style={{ color: 'var(--text-muted)', margin: '4px 0 6px' }}>
            Default: {SLOT_FORMAT_LABELS[formatRules.defaultFormat]} for every CTD section
            without a rule below. Enforced at validation (FMT-SLOT-01) and at compile.
          </div>
          {formatRules.rules.length === 0 ? (
            <div style={{ color: 'var(--text-muted)' }} data-testid="format-rules-empty">
              No section-specific format rules for this region — every slot ships PDF.
            </div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="format-rules-list">
              {formatRules.rules.map((r) => (
                <li key={r.id} style={{ marginBottom: 2 }}>
                  <code>CTD {r.ctdSectionPrefix}</code> —{' '}
                  <strong>{SLOT_FORMAT_LABELS[r.format]}</strong>
                  {!r.isDefault && ' (custom)'}
                  {r.notes !== null && (
                    <span style={{ color: 'var(--text-muted)' }}> · {r.notes}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>
      )}

      {!loading && data !== null && (
        <div data-testid="requirements-table-wrapper">
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
            data-testid="requirements-table"
          >
            <thead>
              <tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Origin</th>
                <th style={thStyle}>Activity</th>
                {(canWrite || canDelete) && <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {/* ── Forms section ─────────────────────────────────────── */}
              {formsCount > 0 && (
                <tr>
                  <td colSpan={(canWrite || canDelete) ? 6 : 5} style={sectionHeaderStyle}>
                    📋 Required Forms ({formsCount})
                  </td>
                </tr>
              )}
              {data.forms.map((f) => (
                <tr key={f.id} data-testid={`form-row-${f.id}`}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 11 }}>
                    {f.formType}
                  </td>
                  <td style={tdStyle}>
                    {f.displayName}
                    {f.isOverride && (
                      <span
                        data-testid={`form-modified-${f.id}`}
                        title={
                          f.baseline
                            ? `Default: ${f.baseline.required ? 'Required' : f.baseline.conditional ? 'Conditional' : 'Optional'}`
                            : 'Modified from the built-in default'
                        }
                        style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#b45309' }}
                      >
                        ✎ modified from default
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <RequiredBadge isRequired={f.required} conditional={f.conditional ?? null} />
                  </td>
                  <td style={tdStyle}>
                    <SourceBadge tenantId={f.tenantId} />
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-muted)' }} data-testid={`form-activity-${f.id}`}>
                    {f.activityCode ?? 'All'}
                  </td>
                  {(canWrite || canDelete) && (
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {/* phase-forms-workflow-follow-up (Item C) — a form
                          requirement's STATUS/scope can be overridden here (the
                          fillable-PDF asset itself is still managed on the
                          Government Forms tab). */}
                      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {canWrite && (
                          <button
                            type="button"
                            data-testid={`edit-form-${f.id}`}
                            onClick={() => setEditRow(editableFromForm(f))}
                            style={editBtnStyle}
                          >
                            Edit
                          </button>
                        )}
                        {canWrite && f.isOverride && (
                          <button
                            type="button"
                            data-testid={`reset-form-${f.id}`}
                            title="Remove the override and restore the built-in default."
                            onClick={() => void handleReset('form', f.id)}
                            style={resetBtnStyle}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}

              {/* ── Templates section ──────────────────────────────────── */}
              {templatesCount > 0 && (
                <tr>
                  <td colSpan={(canWrite || canDelete) ? 6 : 5} style={sectionHeaderStyle}>
                    📄 Required Templates ({templatesCount})
                  </td>
                </tr>
              )}
              {filteredTemplates.map((t) => (
                <tr key={t.id} data-testid={`template-row-${t.id}`}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 11 }}>
                    {t.templateType}
                  </td>
                  <td style={tdStyle}>
                    {t.templateName}
                    {t.isOverride && (
                      <span
                        data-testid={`template-modified-${t.id}`}
                        title={
                          t.baseline
                            ? `Default: ${t.baseline.isRequired ? 'Required' : t.baseline.conditional ? 'Conditional' : 'Optional'} — "${t.baseline.templateName}"`
                            : 'Modified from the built-in default'
                        }
                        style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#b45309' }}
                      >
                        ✎ modified from default
                      </span>
                    )}
                    {t.notes && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {t.notes}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <RequiredBadge isRequired={t.isRequired} conditional={t.conditional} />
                  </td>
                  <td style={tdStyle}>
                    <SourceBadge tenantId={t.tenantId} />
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-muted)' }} data-testid={`template-activity-${t.id}`}>
                    {t.activityCode ?? 'All'}
                  </td>
                  {(canWrite || canDelete) && (
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {/* phase-forms-workflow-follow-up (Item C) — Edit a built-in
                          creates a tenant override; an overridden row edits the
                          override and offers Reset; a custom row edits + deletes
                          in place. */}
                      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {canWrite && (
                          <button
                            type="button"
                            data-testid={`edit-template-${t.id}`}
                            onClick={() => setEditRow(editableFromTemplate(t))}
                            style={editBtnStyle}
                          >
                            Edit
                          </button>
                        )}
                        {canWrite && t.isOverride && (
                          <button
                            type="button"
                            data-testid={`reset-template-${t.id}`}
                            title="Remove the override and restore the built-in default."
                            onClick={() => void handleReset('template', t.id)}
                            style={resetBtnStyle}
                          >
                            Reset
                          </button>
                        )}
                        {canDelete && t.tenantId !== null && !t.isOverride && (
                          <button
                            type="button"
                            data-testid={`delete-template-${t.id}`}
                            onClick={() => void handleDelete(t.id)}
                            style={deleteBtnStyle}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}

              {formsCount === 0 && templatesCount === 0 && (
                <tr>
                  <td
                    colSpan={(canWrite || canDelete) ? 6 : 5}
                    style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '32px 12px' }}
                    data-testid="requirements-empty"
                  >
                    No requirements defined for {region} — {submissionType}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* phase-forms-workflow-follow-up (Item C) — edit / override modal. */}
      {editRow && (
        <RequirementEditModal
          row={editRow}
          activities={activities}
          onSaved={() => {
            setEditRow(null);
            void load();
          }}
          onCancel={() => setEditRow(null)}
        />
      )}

      {/* ── Add-row modal ─────────────────────────────────────────────────── */}
      {addOpen && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="formTemplates.addRequirement"
          title="Add requirement entry"
          ariaLabel="Add requirement entry"
          testId="add-template-modal"
          onClose={() => setAddOpen(false)}
          width={480}
          maxWidth="96vw"
          zIndex={9999}
        >
          <>

            {addError && (
              <div role="alert" style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>
                {addError}
              </div>
            )}

            <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
              Region
              <select
                value={addForm.region}
                onChange={(e) => {
                  const r = e.target.value;
                  const types = SUBMISSION_TYPES[r] ?? [];
                  setAddForm((f) => ({ ...f, region: r, submissionType: types[0] ?? '' }));
                }}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)' }}
              >
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>

            <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
              Submission type
              <select
                value={addForm.submissionType}
                onChange={(e) => setAddForm((f) => ({ ...f, submissionType: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)' }}
              >
                {(SUBMISSION_TYPES[addForm.region] ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>

            {/* R1 — activity tag. '' = applies to every activity of the type.
                Options come from the catalogue for the CURRENT tab selection
                (the modal's region/type usually match it). */}
            <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
              Activity (optional)
              <select
                value={addForm.activityCode}
                onChange={(e) => setAddForm((f) => ({ ...f, activityCode: e.target.value }))}
                data-testid="add-activityCode"
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)' }}
              >
                <option value="">All activities of this type</option>
                {activities.map((a) => (
                  <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
                ))}
              </select>
            </label>

            {/* FT-SR-4 — Template type is a dropdown of defined values, not
                free text, so entries stay consistent. */}
            <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
              Template type
              <select
                value={addForm.templateType}
                onChange={(e) => setAddForm((f) => ({ ...f, templateType: e.target.value }))}
                data-testid="add-templateType"
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)', boxSizing: 'border-box' }}
              >
                <option value="">— Select template type —</option>
                {TEMPLATE_TYPES.map((tt) => (
                  <option key={tt} value={tt}>
                    {tt}
                  </option>
                ))}
              </select>
            </label>

            {/* FT-SR-3 + FT-SR-5 — Template name: dynamic placeholder based on
                the selected region/type, plus a typeahead of existing template
                names (filtered by region + type) and a warning when the typed
                name matches no existing template. */}
            {(() => {
              const matching = allTemplates.filter((t) => {
                const regionOk = regionOfTemplate(t) === addForm.region || (t.Region ?? '') === '';
                const typeOk =
                  addForm.templateType === '' ||
                  (t.TemplateType ?? '').toLowerCase() === addForm.templateType.toLowerCase();
                return regionOk && typeOk;
              });
              const typed = addForm.templateName.trim();
              const nameExists =
                typed === '' ||
                allTemplates.some((t) => t.Name.toLowerCase() === typed.toLowerCase());
              const placeholder = `e.g. ${addForm.region} ${addForm.templateType || 'Cover Letter'}`;
              return (
                <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
                  Template name
                  <input
                    type="text"
                    value={addForm.templateName}
                    onChange={(e) => setAddForm((f) => ({ ...f, templateName: e.target.value }))}
                    placeholder={placeholder}
                    list="sr-template-name-options"
                    data-testid="add-templateName"
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)', boxSizing: 'border-box' }}
                  />
                  <datalist id="sr-template-name-options">
                    {matching.map((t) => (
                      <option key={t.Id} value={t.Name} />
                    ))}
                  </datalist>
                  {!nameExists && (
                    <div
                      data-testid="add-templateName-warning"
                      style={{ marginTop: 4, fontSize: 11, color: 'var(--amber, #b45309)' }}
                    >
                      ⚠ No existing template named &ldquo;{typed}&rdquo;. Check the name or create
                      the template first.
                    </div>
                  )}
                </label>
              );
            })()}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={addForm.isRequired}
                onChange={(e) => setAddForm((f) => ({ ...f, isRequired: e.target.checked }))}
                data-testid="add-isRequired"
              />
              Required (uncheck = optional/conditional)
            </label>

            {!addForm.isRequired && (
              <label style={{ display: 'block', fontSize: 13, marginBottom: 10 }}>
                Condition (shown in tooltip)
                <input
                  type="text"
                  value={addForm.conditional}
                  onChange={(e) => setAddForm((f) => ({ ...f, conditional: e.target.value }))}
                  placeholder="Only if…"
                  data-testid="add-conditional"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)', boxSizing: 'border-box' }}
                />
              </label>
            )}

            <label style={{ display: 'block', fontSize: 13, marginBottom: 16 }}>
              Notes (optional)
              <input
                type="text"
                value={addForm.notes}
                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="add-notes"
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)', boxSizing: 'border-box' }}
              />
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                disabled={addSaving}
                style={{ padding: '6px 16px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="add-submit-btn"
                onClick={() => void handleAddSubmit()}
                disabled={addSaving || !addForm.templateType.trim() || !addForm.templateName.trim()}
                style={{
                  padding: '6px 16px',
                  background: 'var(--primary, #1a3d6b)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontWeight: 600,
                  cursor: addSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {addSaving ? 'Saving…' : 'Add'}
              </button>
            </div>
          </>
        </MovableDialog>
      )}
    </section>
  );
}
